import { useTranslation } from '@/contexts/i18n-context';
import styles from './photo-upload.module.css';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { BodyLocationType, ImageFileDtoBodyLocation } from '@/api/api.schemas';
import { MAX_PHOTO_COUNT } from '@/util/constants';
import { useGetEncountersPhotos } from '@/api/get-photos';
import { useEncounter } from '@/contexts/encounter-context';
import z from 'zod';
import { requiredEnum } from '@/util/validators';
import { useField } from '@/components/form';
import { ErrorField } from '@/components/error-field';
import { PhotoWrapper } from './photo-wrapper';
import { DropZone } from '@/components/uploads/drop-zone';
import { useCreateEncountersImages } from '@/api/create-image';
import { CreateImageErrorResponse, CreateImageErrorType } from '@/api/api.schemas';
import { Loader } from '@/components/loaders/loader';
import { FileInput } from '../file-input';
import { RegularButtonProps } from '@/components/button';
import dropZoneStyles from '../drop-zone.module.css';

const translationBase = 'visit.photoUpload';

export const photoSchema = z.object({
  id: z.number().optional(),
  bodyLocation: requiredEnum(BodyLocationType),
  description: z.string().optional(),
});

export type PhotoFormValues = z.infer<typeof photoSchema>;

export type Photo = {
  id?: number;
  imageSrc: string;
  bodyLocation?: ImageFileDtoBodyLocation;
  description?: string;
  file?: File;
};

interface PhotoUploadProps {
  name: string;
  title: string;
  subText?: string | null;
}

export function PhotoUpload(componentsProps: PhotoUploadProps) {
  const { encounter } = useEncounter();
  const { data: photosResponse, isFetching } = useGetEncountersPhotos(encounter.id);
  const { setValue, error, isSubmitted, fieldProps } = useField<Photo[]>(
    componentsProps.name,
    undefined,
  );
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string>();
  const [photos, setPhotos] = useState<Photo[]>([]);

  const { mutateAsync: uploadImage } = useCreateEncountersImages();

  useEffect(() => {
    const loadedPhotos = photosResponse?.files ?? [];
    setPhotos(loadedPhotos);
    setValue(loadedPhotos);
  }, [photosResponse, setValue]);

  const handleUpdatePhotos = useCallback(
    (newValue: Photo[]) => {
      setPhotos(newValue);
      setValue(newValue, { shouldDirty: true });
    },
    [setValue],
  );

  if (isFetching) {
    return <Loader type="small" />;
  }

  const handleFilesSelected = async (files: File[]) => {
    setUploadError(undefined);
    const fileArray = files.slice(0, MAX_PHOTO_COUNT - photos.length).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    handleUpdatePhotos([
      ...photos,
      ...fileArray.map((file) => ({
        imageSrc: file.url,
      })),
    ]);

    let hasInvalidImage = false;

    const results = await Promise.all(
      fileArray.map(async (file) => {
        try {
          return await uploadImage({
            encounterId: encounter.id,
            data: {
              file: file.file,
            },
          });
        } catch (error) {
          const errorResponse = error as CreateImageErrorResponse;
          if (
            errorResponse.type === CreateImageErrorType.InvalidImage ||
            errorResponse.type === CreateImageErrorType.ValidationFailed
          ) {
            hasInvalidImage = true;
          }
          return {
            id: undefined,
            imageSrc: file.url,
          };
        }
      }),
    );

    const resultFilter = results.filter((photo) => photo.id !== undefined);

    if (resultFilter.length !== fileArray.length) {
      setUploadError(
        hasInvalidImage
          ? `${translationBase}.error.unsupportedFormat`
          : `${translationBase}.error.upload`,
      );
    }
    handleUpdatePhotos([...photos, ...resultFilter]);
  };

  const handleUpdate = (imageId: number, bodyLocation: BodyLocationType, description?: string) => {
    handleUpdatePhotos(
      photos.map((photo) =>
        photo.id === imageId ? { ...photo, bodyLocation, description } : photo,
      ),
    );
  };

  const handleDelete = (imageId: number) => {
    handleUpdatePhotos(photos.filter((photo) => photo.id !== imageId));
  };

  const handleDrop = async (e: React.DragEvent) => {
    stopEventPropagation(e);
    handleFilesSelected(Array.from(e.dataTransfer.files));
  };

  const stopEventPropagation = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const hasBodyLocationError = Array.isArray(error) && error.some((err) => err?.bodyLocation);
  const displayError =
    isSubmitted &&
    (hasBodyLocationError
      ? { message: t(`${translationBase}.error.noBodyLocation`), type: 'custom' }
      : error);

  const hasPhotos = photos.length > 0;

  const buttonProps: RegularButtonProps = {
    leftIcon: 'add',
    size: 'S',

    ...(hasPhotos
      ? {
          variant: 'none',
          className: clsx(dropZoneStyles.uploadMoreButton, dropZoneStyles.photos),
          children: t('common.uploadMore'),
          onDragOver: stopEventPropagation,
          onDrop: handleDrop,
        }
      : {
          variant: 'outlined',
          className: dropZoneStyles.uploadButton,
          children: t(`${translationBase}.dropZone.uploadButton`),
        }),
  };

  return (
    <div className={styles.photoUploadContainer} data-testid="condition-details-photo-upload">
      <div className={styles.titleSection}>
        <h3 className={styles.title}>{componentsProps.title}</h3>
        {componentsProps.subText && (
          <p className={clsx(styles.description, 'body-3')}>{componentsProps.subText}</p>
        )}
        {!!displayError && <ErrorField error={displayError} />}
      </div>
      <div
        onDrop={(e) => {
          handleDrop(e);
          setIsDragging(false);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => {
          setIsDragging(false);
        }}
        className={clsx(styles.photoDropZone, isDragging && styles.dragging)}
        role="presentation"
        aria-label={t(`${translationBase}.dropZone.ariaLabel`)}
      >
        <DropZone
          title={t(`${translationBase}.dropZone.title`)}
          ref={fieldProps.ref}
          onChange={handleFilesSelected}
          description={t(`${translationBase}.uploadPhotoButton.description`)}
          hasFiles={hasPhotos}
          showUploadMoreButton={photos.length < MAX_PHOTO_COUNT}
          inputField={
            <FileInput onFilesSelected={handleFilesSelected} multiple buttonProps={buttonProps} />
          }
          error={uploadError}
        >
          {photos.map((photo, index) => (
            <PhotoWrapper
              key={photo.id || photo.imageSrc}
              alt={photo.description ?? t(`${translationBase}.photoAlt`, { index: index + 1 })}
              photo={photo}
              onDelete={handleDelete}
              handleUpdate={handleUpdate}
            />
          ))}
        </DropZone>
      </div>
    </div>
  );
}
